import {
  cardQueueUpdatedAt,
  feedItemToCard,
  isClosedQueueCard,
} from "@/lib/financialWork/feedClient";
import { queueRowSortMetrics } from "@/lib/financialWork/queueRowSort";
import type {
  FinancialWorkFeedItem,
  LiffFinancialWorkCard,
} from "@/lib/financialWork/types";
import { getBangkokTodayDateKey } from "@/lib/transactionSummary";

const BANGKOK_TZ = "Asia/Bangkok";

export type WorkOverviewTotals = {
  outstandingMinor: number;
  monthIncomeMinor: number;
  monthOutMinor: number;
};

/** Gregorian year + 1–12 month in Asia/Bangkok. */
export function getBangkokYearMonth(date = new Date()): {
  year: number;
  month: number;
} {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: BANGKOK_TZ,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(date);
  const year = Number(parts.find((part) => part.type === "year")?.value);
  const month = Number(parts.find((part) => part.type === "month")?.value);
  if (!Number.isFinite(year) || !Number.isFinite(month)) {
    const fallback = getBangkokTodayDateKey(date);
    return {
      year: Number(fallback.slice(0, 4)),
      month: Number(fallback.slice(5, 7)),
    };
  }
  return { year, month };
}

/** Inclusive Bangkok calendar range for a Gregorian year + 1–12 month. */
export function getBangkokMonthRangeFromParts(
  year: number,
  month: number,
): { from: string; to: string } {
  const from = `${year}-${String(month).padStart(2, "0")}-01`;
  const last = new Date(Date.UTC(year, month, 0));
  const to = `${last.getUTCFullYear()}-${String(last.getUTCMonth() + 1).padStart(2, "0")}-${String(last.getUTCDate()).padStart(2, "0")}`;
  return { from, to };
}

export function getBangkokMonthRange(date = new Date()): { from: string; to: string } {
  const { year, month } = getBangkokYearMonth(date);
  return getBangkokMonthRangeFromParts(year, month);
}

export type WorkOverviewPeriod = {
  year: number;
  month: number;
};

function cardBangkokDateKey(card: LiffFinancialWorkCard): string | null {
  const raw = cardQueueUpdatedAt(card);
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: BANGKOK_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(ms));
}

/** Distinct Bangkok year/months that have queue rows, newest first. */
export function listWorkOverviewPeriods(
  cards: LiffFinancialWorkCard[],
  fallback?: WorkOverviewPeriod | null,
): WorkOverviewPeriod[] {
  const keys = new Set<string>();
  for (const card of cards) {
    const dateKey = cardBangkokDateKey(card);
    if (!dateKey) continue;
    keys.add(dateKey.slice(0, 7));
  }
  const periods = [...keys]
    .sort((left, right) => right.localeCompare(left))
    .map((ym) => ({
      year: Number(ym.slice(0, 4)),
      month: Number(ym.slice(5, 7)),
    }))
    .filter(
      (period) =>
        Number.isFinite(period.year) &&
        Number.isFinite(period.month) &&
        period.month >= 1 &&
        period.month <= 12,
    );
  if (periods.length > 0) return periods;
  if (fallback && Number.isFinite(fallback.year) && Number.isFinite(fallback.month)) {
    return [{ year: fallback.year, month: fallback.month }];
  }
  return [];
}

export function listWorkOverviewPeriodsFromFeedItems(
  items: FinancialWorkFeedItem[],
  fallback?: WorkOverviewPeriod | null,
): WorkOverviewPeriod[] {
  return listWorkOverviewPeriods(items.map(feedItemToCard), fallback);
}

function isInBangkokRange(
  dateKey: string | null,
  from: string,
  to: string,
): boolean {
  if (!dateKey) return false;
  if (from && dateKey < from) return false;
  if (to && dateKey > to) return false;
  return true;
}

/** Sum queue money columns from every work-tab row — same rules as the table. */
export function computeWorkOverviewFromCards(
  cards: LiffFinancialWorkCard[],
  options: {
    canonicalRemainingMinor?: number | null;
    monthFrom: string;
    monthTo: string;
  },
): WorkOverviewTotals {
  let monthIncomeMinor = 0;
  let monthOutMinor = 0;

  for (const card of cards) {
    if (!isInBangkokRange(cardBangkokDateKey(card), options.monthFrom, options.monthTo)) {
      continue;
    }
    // Same closed/open money columns as the queue table (เงินเข้า / เงินออกจริง).
    const metrics = queueRowSortMetrics(card, isClosedQueueCard(card));
    monthIncomeMinor += metrics.moneyInMinor;
    monthOutMinor += metrics.moneyOutMinor;
  }

  const outstandingMinor =
    options.canonicalRemainingMinor ??
    cards.reduce((sum, card) => {
      if (isClosedQueueCard(card)) return sum;
      return sum + queueRowSortMetrics(card, false).relatedMinor;
    }, 0);

  return {
    outstandingMinor,
    monthIncomeMinor,
    monthOutMinor,
  };
}

export function feedItemsToCards(items: FinancialWorkFeedItem[]): LiffFinancialWorkCard[] {
  return items.map(feedItemToCard);
}

export function computeWorkOverviewFromFeedItems(
  items: FinancialWorkFeedItem[],
  options: Parameters<typeof computeWorkOverviewFromCards>[1],
): WorkOverviewTotals {
  return computeWorkOverviewFromCards(feedItemsToCards(items), options);
}
